import React, {
  ButtonHTMLAttributes,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

import {
  Button,
  Display,
  Header,
  KDSButton,
  Spacer,
  Text,
  theme,
  Icon,
} from '@kueski-dev/kueski-design-system';

import useClickOutside from 'Hooks/useClickOutside';
import {
  BottomSheetCard,
  BottomSheetCardContent,
  BottomSheetContainer,
  BottomSheetHeader,
  BottomSheetOverlay,
} from './BottomSheetModal.styles';

interface BottomSheetModalActionsProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  'data-testid'?: string;
}

interface BottomSheetModalProps {
  acceptButtonProps?: BottomSheetModalActionsProps;
  cancelButtonProps?: BottomSheetModalActionsProps;
  description?: string | ReactNode;
  isMini?: boolean;
  onClose: () => void;
  open: boolean;
  title?: string;
}

const BottomSheetModal: React.FC<PropsWithChildren<BottomSheetModalProps>> = ({
  acceptButtonProps,
  cancelButtonProps,
  children,
  description,
  isMini,
  onClose,
  open,
  title,
}) => {
  const optionsRef = useRef(null);

  useClickOutside(optionsRef, onClose);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : 'unset';
  }, [open]);

  if (!open) return null;

  return createPortal(
    <BottomSheetContainer isMini={isMini}>
      <BottomSheetOverlay />
      <BottomSheetCard ref={optionsRef} isMini={isMini}>
        <BottomSheetCardContent isMini={isMini}>
          {title && (
            <BottomSheetHeader>
              <Header
                $align={['left', 'tablet:center']}
                $color="textPrimary"
                $format={isMini ? 'Title/Large/Bold' : ['Title/Large/Bold', 'tablet:Headline/Large/Bold']}
              >
                {title}
              </Header>
              <Icon
                className="close-icon"
                color={theme.color.iconPrimary}
                height={16}
                name="Close"
                onClick={onClose}
                width={16}
              />
            </BottomSheetHeader>
          )}
          {description && (
            <>
              <Spacer $size="spacing05" />
              <Text
                $align={isMini ? 'left' : ['left', 'tablet:center']}
                $color="textSecondary"
                $format={['Body/Medium/Regular', 'tablet:Body/Large/Regular']}
              >
                {description}
              </Text>
            </>
          )}
          {isMini && cancelButtonProps?.label && acceptButtonProps?.label && <Spacer $size="spacing06" />}
          <Display $on={isMini ? ['inline', 'tablet:flex'] : 'inline'} className="actions">
            {cancelButtonProps?.label && (
              <>
                {!isMini && <Spacer $size={['spacing06', 'tablet:spacing09']} />}
                <Display $on={['inline', 'tablet:none']}>
                  <Button $type={KDSButton.LINK} $wide type="button" {...cancelButtonProps}>
                    {cancelButtonProps.label}
                  </Button>
                </Display>

                <Display $on={['none', 'tablet:contents']}>
                  <Button $type={KDSButton.OUTLINE} $wide type="button" {...cancelButtonProps}>
                    {cancelButtonProps.label}
                  </Button>
                </Display>
              </>
            )}
            {acceptButtonProps?.label && (
              <>
                {!isMini && <Spacer $size="spacing04" />}
                <Button $type={KDSButton.PRIMARY} $wide type="button" {...acceptButtonProps}>
                  {acceptButtonProps.label}
                </Button>
              </>
            )}
          </Display>
          {children}
        </BottomSheetCardContent>
      </BottomSheetCard>
    </BottomSheetContainer>,
    document.body,
  );
};

BottomSheetModal.defaultProps = {
  acceptButtonProps: { label: '' },
  cancelButtonProps: { label: '' },
  description: '',
  isMini: false,
  title: '',
};

export default BottomSheetModal;
